<template>
  <div class="template-manager">
    <!-- Template Manager Dialog -->
    <TemplateManagerDialog
      v-model="showTemplateManagerDialog"
      :available-templates="availableTemplates"
      :loading-templates="loadingTemplates"
      :template-filter="templateFilter"
      :template-type-filter="templateTypeFilter"
      :template-filter-type-options="templateFilterTypeOptions"
      @update:template-filter="templateFilter = $event"
      @update:template-type-filter="templateTypeFilter = $event"
      @hide="onTemplateManagerClose"
      @edit-template="editTemplateFromManager"
      @view-compound-template="viewCompoundTemplate"
      @toggle-template-status="toggleTemplateStatus"
      @delete-template="deleteTemplate"
      @show-template-details="showTemplateDetails"
      @refresh="loadTemplates"
    />

    <!-- Template Editor Dialog -->
    <TemplateEditor
      v-model="showTemplateEditor"
      :template-id="selectedTemplateId"
      :template-type="selectedTemplateType"
      :site-id="siteId"
      @template-updated="onTemplateUpdated"
      @template-deleted="onTemplateDeleted"
      @update:model-value="onTemplateEditorClose"
    />

    <!-- Save As Template Dialog -->
    <SaveAsTemplateDialog
      v-model="showSaveAsTemplateDialog"
      :form-data="templateForm"
      :saving="savingTemplate"
      @update:name="templateForm.name = $event"
      @update:description="templateForm.description = $event"
      @update:include-devices="templateForm.includeDevices = $event"
      @update:include-points="templateForm.includePoints = $event"
      @update:scope="templateForm.scope = $event"
      @submit="confirmSaveAsTemplate"
    />

    <!-- Compound Template Dialog -->
    <CompoundTemplateDialog
      v-model="showCompoundTemplateDialog"
      :templates="compoundTemplates"
      :selected-template="selectedCompoundTemplate"
      :template-data="selectedCompoundTemplateData"
      :form-data="compoundFormData"
      :loading-templates="loadingTemplates"
      :saving="savingCompound"
      @update:selected-template="selectedCompoundTemplate = $event"
      @update:name="compoundFormData.name = $event"
      @update:description="compoundFormData.description = $event"
      @submit="createFromTemplate"
    />

    <!-- Template Usage Locations Dialog -->
    <TemplateUsageDialog
      v-model="showUsageLocationsDialog"
      :template-name="usageTemplate?.name || ''"
      :locations="usageLocations"
      :columns="usageLocationColumns"
      :deleting-item="deletingUsageItem"
      :deleting-all="deletingAllUsages"
      :deleting-template="deletingTemplate"
      @delete-item="deleteUsageItem"
      @delete-all="confirmDeleteAllUsages"
      @retry-delete-template="retryDeleteTemplate"
      @close="closeUsageDialog"
    />
  </div>
</template>

<script setup>
  import { ref, computed, inject, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import { useTemplateApi } from '@/composables/brick-schema/api';
  import { useTemplateProtection } from '@/composables/brick-schema';

  // Import dialog components
  import TemplateManagerDialog from '../dialogs/template/TemplateManagerDialog.vue';
  import TemplateEditor from '../../templates/TemplateEditor.vue';
  import SaveAsTemplateDialog from '../dialogs/SaveAsTemplateDialog.vue';
  import CompoundTemplateDialog from '../dialogs/CompoundTemplateDialog.vue';
  import TemplateUsageDialog from '../dialogs/TemplateUsageDialog.vue';

  // Props
  const props = defineProps({
    siteId: {
      type: [String, Number],
      required: true,
    },
  });

  // Emits
  const emit = defineEmits([
    'refresh-data',
    'template-saved',
    'template-updated',
    'template-deleted',
    'compound-created',
  ]);

  // Inject shared context from parent
  const schemaContext = inject('schemaContext', null);

  // Quasar
  const $q = useQuasar();

  // Composables
  const siteIdRef = computed(() => props.siteId);
  const templateApi = useTemplateApi(siteIdRef);
  const { getTemplateInfo } = useTemplateProtection();

  // ===================================
  // DIALOG VISIBILITY STATES
  // ===================================
  const showTemplateManagerDialog = ref(false);
  const showTemplateEditor = ref(false);
  const showSaveAsTemplateDialog = ref(false);
  const showCompoundTemplateDialog = ref(false);
  const showUsageLocationsDialog = ref(false);

  // ===================================
  // LOADING/SAVING STATES
  // ===================================
  const loadingTemplates = ref(false);
  const savingTemplate = ref(false);
  const savingCompound = ref(false);
  const deletingUsageItem = ref(false);
  const deletingAllUsages = ref(false);
  const deletingTemplate = ref(false);

  // ===================================
  // TEMPLATE DATA
  // ===================================
  const availableTemplates = ref([]);
  const compoundTemplates = ref([]);
  const templateFilter = ref('');
  const templateTypeFilter = ref('');

  const templateFilterTypeOptions = [
    { label: 'All Types', value: '' },
    { label: 'Equipment', value: 'equipment' },
    { label: 'Device', value: 'device' },
    { label: 'Compound', value: 'compound' },
  ];

  // ===================================
  // TEMPLATE EDITOR STATE
  // ===================================
  const selectedTemplateId = ref(null);
  const selectedTemplateType = ref(null);

  // ===================================
  // SAVE AS TEMPLATE FORM
  // ===================================
  const templateForm = ref({
    name: '',
    description: '',
    type: 'equipment',
    includeDevices: true,
    includePoints: true,
    scope: 'site',
    itemData: null,
    deviceCount: 0,
    pointCount: 0,
  });

  // ===================================
  // COMPOUND TEMPLATE STATE
  // ===================================
  const selectedCompoundTemplate = ref(null);
  const selectedSpace = ref(null);
  const compoundFormData = ref({
    name: '',
    description: '',
    brick_class: '',
    compound_type: 'template',
    parent_role: 'controller',
    components: [],
  });

  const selectedCompoundTemplateData = computed(() => {
    if (!selectedCompoundTemplate.value) {return null;}
    return compoundTemplates.value.find((t) => t.id === selectedCompoundTemplate.value);
  });

  // ===================================
  // TEMPLATE USAGE STATE
  // ===================================
  const usageLocations = ref([]);
  const usageTemplate = ref(null);
  const usageLocationColumns = [
    { name: 'name', label: 'Name', field: 'name', align: 'left', sortable: true },
    { name: 'type', label: 'Type', field: 'type', align: 'center', sortable: true },
    { name: 'location', label: 'Location', field: (row) => row, align: 'left' },
    { name: 'actions', label: 'Actions', field: 'actions', align: 'center' },
  ];

  // ===================================
  // WATCHERS
  // ===================================
  watch(showCompoundTemplateDialog, async (newVal) => {
    if (newVal) {
      await loadCompoundTemplates();
    }
  });

  // ===================================
  // TEMPLATE LOADING
  // ===================================

  async function loadTemplates() {
    loadingTemplates.value = true;
    try {
      const templates = await templateApi.loadAllTemplates();
      availableTemplates.value = templates || [];
    } catch (error) {
      availableTemplates.value = [];
      $q.notify({
        type: 'negative',
        message: 'Failed to load templates',
      });
    } finally {
      loadingTemplates.value = false;
    }
  }

  async function loadCompoundTemplates() {
    loadingTemplates.value = true;
    try {
      const rawTemplates = await templateApi.loadCompoundTemplates();

      compoundTemplates.value = rawTemplates.map((template) => ({
        ...template,
        id: template.id,
        name: template.name,
        label: template.name,
        value: template.id,
        type: 'compound',
        brick_class: template.brick_class || 'brick:Equipment',
        components: template.components || template.devices || [],
        component_types: template.component_types || template.components?.map((c) => c.brick_class || c.type) || [],
        description: template.description || `Compound assembly with ${template.component_count || template.components?.length || 0} components`,
      }));
    } catch (error) {
      compoundTemplates.value = [];
    } finally {
      loadingTemplates.value = false;
    }
  }

  // ===================================
  // TEMPLATE MANAGER OPERATIONS
  // ===================================

  function showTemplateManager() {
    showTemplateManagerDialog.value = true;
    loadTemplates();
    loadCompoundTemplates();
  }

  function onTemplateManagerClose() {
    loadCompoundTemplates();
  }

  function editTemplateFromManager(template) {
    selectedTemplateId.value = template.id;
    selectedTemplateType.value = template.template_type || 'equipment';
    showTemplateManagerDialog.value = false;
    showTemplateEditor.value = true;
  }

  function viewCompoundTemplate(template) {
    $q.dialog({
      title: `Compound Template: ${template.name}`,
      message: `
      <div class="q-pa-md">
        <p><strong>Description:</strong> ${template.description || 'No description'}</p>
        <p><strong>Components:</strong> ${template.component_count || template.components?.length || 0}</p>
        <p><strong>BRICK Class:</strong> ${template.brick_class || 'Not specified'}</p>
      </div>
    `,
      html: true,
      ok: true,
    });
  }

  async function toggleTemplateStatus(template) {
    try {
      const newStatus = !template.is_active;

      await authenticatedFetch(`/api/v2/templates/${template.template_type}/${template.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus }),
      });

      const index = availableTemplates.value.findIndex((t) => t.id === template.id);
      if (index !== -1) {
        availableTemplates.value[index].is_active = newStatus;
      }

      $q.notify({
        type: 'positive',
        message: `Template ${newStatus ? 'activated' : 'deactivated'}`,
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to update template status',
      });
    }
  }

  async function deleteTemplate(template) {
    $q.dialog({
      title: 'Delete Template',
      message: `Are you sure you want to delete template "${template.name}"?`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v2/templates/${template.template_type}/${template.id}`,
          { method: 'DELETE' },
        );

        if (response.ok) {
          availableTemplates.value = availableTemplates.value.filter((t) => t.id !== template.id);

          $q.notify({
            type: 'positive',
            message: 'Template deleted successfully',
          });

          emit('template-deleted');
        } else {
          const errorData = await response.json();

          if (errorData.usage_locations) {
            usageLocations.value = errorData.usage_locations;
            usageTemplate.value = template;
            showUsageLocationsDialog.value = true;
          } else {
            throw new Error(errorData.message || 'Failed to delete template');
          }
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: error.message || 'Failed to delete template',
        });
      }
    });
  }

  function showTemplateDetails(template) {
    $q.dialog({
      title: template.name,
      message: `
      <div class="q-pa-md">
        <p><strong>Type:</strong> ${template.template_type}</p>
        <p><strong>BRICK Class:</strong> ${template.brick_class}</p>
        <p><strong>Description:</strong> ${template.description || 'No description'}</p>
        <p><strong>Status:</strong> ${template.is_active ? 'Active' : 'Inactive'}</p>
        <p><strong>Usage Count:</strong> ${template.usage_count || 0}</p>
        <p><strong>Created:</strong> ${new Date(template.created_at).toLocaleDateString()}</p>
      </div>
    `,
      html: true,
      ok: true,
    });
  }

  // ===================================
  // TEMPLATE EDITOR OPERATIONS
  // ===================================

  function onTemplateUpdated() {
    loadTemplates();
    emit('template-updated');
    emit('refresh-data');
  }

  function onTemplateDeleted() {
    loadTemplates();
    emit('template-deleted');
  }

  function onTemplateEditorClose(isOpen) {
    if (!isOpen) {
      selectedTemplateId.value = null;
      selectedTemplateType.value = null;
    }
  }

  // ===================================
  // SAVE AS TEMPLATE OPERATIONS
  // ===================================

  function saveAsTemplate(node) {
    const templateInfo = {
      id: node.data?.id || node.id,
      name: node.label || node.data?.name || '',
      type: node.type,
      brick_class: node.data?.brick_class || '',
      deviceCount: node.children?.filter((c) => c.type === 'device').length || 0,
      pointCount: countPoints(node),
    };

    templateForm.value = {
      name: `${templateInfo.name} Template`,
      description: '',
      type: node.type === 'device' ? 'device' : 'equipment',
      includeDevices: true,
      includePoints: true,
      scope: 'site',
      itemData: templateInfo,
      deviceCount: templateInfo.deviceCount,
      pointCount: templateInfo.pointCount,
    };

    showSaveAsTemplateDialog.value = true;
  }

  function countPoints(node) {
    let count = 0;

    if (node.children) {
      node.children.forEach((child) => {
        if (child.type === 'point') {
          count++;
        } else if (child.type === 'device') {
          count += countPoints(child);
        }
      });
    }

    return count;
  }

  async function confirmSaveAsTemplate() {
    if (!templateForm.value.name) {
      $q.notify({
        type: 'warning',
        message: 'Please enter a template name',
      });
      return;
    }

    savingTemplate.value = true;
    try {
      const templateData = {
        name: templateForm.value.name,
        description: templateForm.value.description,
        scope: templateForm.value.scope,
        include_devices: templateForm.value.includeDevices,
        include_points: templateForm.value.includePoints,
      };

      const endpoint = templateForm.value.type === 'device'
        ? `/api/v2/sites/${props.siteId}/devices/${templateForm.value.itemData.id}/save-as-template`
        : `/api/v2/sites/${props.siteId}/equipment/${templateForm.value.itemData.id}/save-as-template`;

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        showSaveAsTemplateDialog.value = false;
        resetTemplateForm();

        $q.notify({
          type: 'positive',
          message: 'Template saved successfully',
        });

        emit('template-saved');
        loadTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save template');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to save template',
      });
    } finally {
      savingTemplate.value = false;
    }
  }

  function resetTemplateForm() {
    templateForm.value = {
      name: '',
      description: '',
      type: 'equipment',
      includeDevices: true,
      includePoints: true,
      scope: 'site',
      itemData: null,
      deviceCount: 0,
      pointCount: 0,
    };
  }

  // ===================================
  // COMPOUND TEMPLATE OPERATIONS
  // ===================================

  function showCompoundTemplateDialogForSpace(node) {
    selectedSpace.value = node.data || node;
    selectedCompoundTemplate.value = null;
    compoundFormData.value = {
      name: '',
      description: '',
      brick_class: '',
      compound_type: 'template',
      parent_role: 'controller',
      components: [],
    };
    showCompoundTemplateDialog.value = true;
  }

  async function createFromTemplate() {
    if (!selectedCompoundTemplate.value || !compoundFormData.value.name) {
      $q.notify({
        type: 'warning',
        message: 'Please select a template and enter a name',
      });
      return;
    }

    const template = compoundTemplates.value.find((t) => t.id === selectedCompoundTemplate.value);
    if (!template) {
      $q.notify({
        type: 'warning',
        message: 'Template not found',
      });
      return;
    }

    savingCompound.value = true;
    try {
      const response = await authenticatedFetch('/api/compound-devices/create-from-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: template.id,
          name: compoundFormData.value.name,
          description: compoundFormData.value.description,
          space_id: selectedSpace.value?.id,
        }),
      });

      if (response.ok) {
        showCompoundTemplateDialog.value = false;

        $q.notify({
          type: 'positive',
          message: 'Compound created from template',
        });

        emit('compound-created');
        emit('refresh-data');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create compound');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to create compound from template',
      });
    } finally {
      savingCompound.value = false;
    }
  }

  // ===================================
  // TEMPLATE USAGE OPERATIONS
  // ===================================

  async function deleteUsageItem(item) {
    deletingUsageItem.value = true;
    try {
      const endpoint = item.type === 'device'
        ? `/api/v2/sites/${props.siteId}/devices/${item.id}`
        : `/api/v2/sites/${props.siteId}/equipment/${item.id}`;

      const response = await authenticatedFetch(endpoint, { method: 'DELETE' });

      if (response.ok) {
        usageLocations.value = usageLocations.value.filter((loc) => loc.id !== item.id);

        $q.notify({
          type: 'positive',
          message: `${item.name} deleted`,
        });

        if (usageLocations.value.length === 0) {
          closeUsageDialog();
        }

        emit('refresh-data');
      } else {
        throw new Error('Failed to delete item');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to delete item',
      });
    } finally {
      deletingUsageItem.value = false;
    }
  }

  function confirmDeleteAllUsages() {
    $q.dialog({
      title: 'Delete All Usages',
      message: `This will delete all ${usageLocations.value.length} items using this template. This action cannot be undone.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete All',
        color: 'negative',
      },
    }).onOk(async () => {
      deletingAllUsages.value = true;
      try {
        for (const item of usageLocations.value) {
          const endpoint = item.type === 'device'
            ? `/api/v2/sites/${props.siteId}/devices/${item.id}`
            : `/api/v2/sites/${props.siteId}/equipment/${item.id}`;

          await authenticatedFetch(endpoint, { method: 'DELETE' });
        }

        usageLocations.value = [];

        $q.notify({
          type: 'positive',
          message: 'All usages deleted',
        });

        closeUsageDialog();
        emit('refresh-data');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to delete some items',
        });
      } finally {
        deletingAllUsages.value = false;
      }
    });
  }

  async function retryDeleteTemplate() {
    if (!usageTemplate.value) {return;}

    deletingTemplate.value = true;
    try {
      const response = await authenticatedFetch(
        `/api/v2/templates/${usageTemplate.value.template_type}/${usageTemplate.value.id}`,
        { method: 'DELETE' },
      );

      if (response.ok) {
        availableTemplates.value = availableTemplates.value.filter(
          (t) => t.id !== usageTemplate.value.id,
        );

        closeUsageDialog();

        $q.notify({
          type: 'positive',
          message: 'Template deleted successfully',
        });

        emit('template-deleted');
      } else {
        throw new Error('Template still has usages');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Template still has usages - delete them first',
      });
    } finally {
      deletingTemplate.value = false;
    }
  }

  function closeUsageDialog() {
    showUsageLocationsDialog.value = false;
    usageLocations.value = [];
    usageTemplate.value = null;
  }

  // ===================================
  // TEMPLATE ACTIONS FROM TREE
  // ===================================

  function showTemplateActions(node) {
    const templateInfo = getTemplateInfo(node);
    if (!templateInfo) {
      $q.notify({
        type: 'negative',
        message: 'Template information not found for this item',
      });
      return;
    }

    $q.dialog({
      title: 'Template Actions',
      message: `
      <div class="q-mb-sm">
        <div class="text-subtitle2">${node.label}</div>
        <div class="text-caption text-grey-7">Template: ${templateInfo.templateName}</div>
      </div>
      <div class="text-body2 q-mb-md">
        Choose an action for this templated ${node.type}:
      </div>
    `,
      html: true,
      options: {
        type: 'radio',
        model: 'edit',
        items: [
          { label: 'Edit Template', value: 'edit', color: 'orange' },
          { label: 'Remove from Template (make independent)', value: 'remove', color: 'negative' },
        ],
      },
      cancel: true,
      persistent: false,
    }).onOk((action) => {
      if (action === 'edit') {
        editEquipmentTemplate(node);
      } else if (action === 'remove') {
        detachFromTemplate(node);
      }
    });
  }

  function editEquipmentTemplate(node) {
    const templateInfo = getTemplateInfo(node);
    if (!templateInfo) {
      $q.notify({
        type: 'negative',
        message: 'Template information not found for this equipment',
      });
      return;
    }

    const template = availableTemplates.value.find(
      (t) => t.id === templateInfo.templateId && t.template_type === templateInfo.templateType,
    );

    if (!template) {
      $q.notify({
        type: 'warning',
        message: 'Template not found. Loading templates...',
      });

      loadTemplates().then(() => {
        const foundTemplate = availableTemplates.value.find(
          (t) => t.id === templateInfo.templateId && t.template_type === templateInfo.templateType,
        );
        if (foundTemplate) {
          editTemplateFromManager(foundTemplate);
        } else {
          $q.notify({
            type: 'negative',
            message: `Template could not be loaded (ID: ${templateInfo.templateId}, Type: ${templateInfo.templateType})`,
          });
        }
      });
      return;
    }

    editTemplateFromManager(template);
  }

  async function detachFromTemplate(node) {
    $q.dialog({
      title: 'Detach from Template',
      message: `Are you sure you want to detach "${node.label}" from its template? This will make it an independent ${node.type}.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Detach',
        color: 'warning',
      },
    }).onOk(async () => {
      try {
        const endpoint = node.type === 'device'
          ? `/api/v2/sites/${props.siteId}/devices/${node.data.id}/detach-template`
          : `/api/v2/sites/${props.siteId}/equipment/${node.data.id}/detach-template`;

        const response = await authenticatedFetch(endpoint, { method: 'POST' });

        if (response.ok) {
          $q.notify({
            type: 'positive',
            message: 'Successfully detached from template',
          });

          emit('refresh-data');
        } else {
          throw new Error('Failed to detach from template');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to detach from template',
        });
      }
    });
  }

  async function syncWithTemplate(node) {
    $q.dialog({
      title: 'Sync with Template',
      message: `This will update "${node.label}" to match the latest template configuration. Any local modifications will be lost.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Sync',
        color: 'primary',
      },
    }).onOk(async () => {
      try {
        const endpoint = node.type === 'device'
          ? `/api/v2/sites/${props.siteId}/devices/${node.data.id}/sync-template`
          : `/api/v2/sites/${props.siteId}/equipment/${node.data.id}/sync-template`;

        const response = await authenticatedFetch(endpoint, { method: 'POST' });

        if (response.ok) {
          $q.notify({
            type: 'positive',
            message: 'Successfully synced with template',
          });

          emit('refresh-data');
        } else {
          throw new Error('Failed to sync with template');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to sync with template',
        });
      }
    });
  }

  // ===================================
  // EXPOSE PUBLIC METHODS
  // ===================================
  defineExpose({
    // Template manager
    showTemplateManager,
    loadTemplates,
    loadCompoundTemplates,

    // Save as template
    saveAsTemplate,

    // Compound template
    showCompoundTemplateDialogForSpace,

    // Template actions from tree
    showTemplateActions,
    editEquipmentTemplate,
    detachFromTemplate,
    syncWithTemplate,
  });
</script>

<style scoped>
.template-manager {
  /* Container styles - invisible wrapper */
}
</style>
